import {Link} from 'quickstart/components/content/Link'
import {DropdownMenu} from 'quickstart/components/controls/DropdownMenu'
import {MetaLink} from 'quickstart/components/tizra/MetaLink'
import {useSearch} from 'quickstart/hooks/useSearch'
import {useSearchConfig} from 'quickstart/hooks/useSearchConfig'
import styled from 'quickstart/styled-components/system'
import {ComponentProps, MouseEvent, ReactNode} from 'react'
import {logger} from 'tizra'
import * as S from './styles'

const log = logger('NavLink')

// Don't include everything from Link, it just makes development more
// complicated.
export interface NavLinkLinkProps {
  children?: ReactNode
  label?: ReactNode
  href?: string
  onClick?: (e: MouseEvent<HTMLElement>) => void
  // catch programming errors
  type?: never
  link?: never
  text?: never
}

// Styled for the sake of polymorphism
const NavLinkLink = styled(Link).attrs<NavLinkLinkProps>(
  ({children, label}) => ({
    children: children || label || '',
    variant: 'nav',
    label: undefined,
  }),
)`` as (props: NavLinkLinkProps) => JSX.Element

export type NavLinkAccountProps = NavLinkLinkProps

const NavLinkAccount = NavLinkLink

export type NavLinkBookshelfProps = NavLinkLinkProps

const NavLinkBookshelf = NavLinkLink

export interface NavLinkExpandoProps {
  metaTypes?: string[]
  adminTag?: string
  sortProp?: string
  sortDirection?: 'ascending' | 'descending'
  // catch programming errors
  type?: never
}

const NavLinkExpando = ({
  metaTypes = ['AdminTagged'],
  adminTag = '',
  sortProp = 'Title',
  sortDirection = 'ascending',
}: NavLinkExpandoProps) => {
  const searchConfig = useSearchConfig({
    mode: 'browse',
    metaTypes: {metadata: metaTypes},
    pre: {
      all: [adminTag],
    },
    fields: {
      depth: {defaultValue: 'metadata'},
      page: {defaultValue: 100},
      sort: {defaultValue: `sortProp-${sortDirection}`},
    },
    sorting: {sortProp},
  })
  const {results} = useSearch(searchConfig)
  if (!results) {
    return <DropdownMenu.Item>(none)</DropdownMenu.Item>
  }
  return (
    <>
      {results.map((r, i) => (
        <DropdownMenu.Item as={MetaLink} metaObj={r} key={i} />
      ))}
    </>
  )
}

export interface NavLinkMenuProps {
  label?: string
  items?: NavLinkProps[]
  menuProps?: ComponentProps<typeof DropdownMenu>
  itemProps?: ComponentProps<typeof DropdownMenu.Item>
  // catch programming errors
  type?: never
}

const NavLinkMenu = ({
  label = '',
  items = [],
  menuProps,
  itemProps,
}: NavLinkMenuProps) => {
  return (
    <DropdownMenu label={label} variant="nav" {...menuProps}>
      {items.map((props, i) =>
        'items' in props ?
          <NavLink key={i} {...itemProps} {...props} />
        : <DropdownMenu.Item key={i} as={NavLink} {...itemProps} {...props} />,
      )}
    </DropdownMenu>
  )
}

export interface NavLinkSeparatorProps {}

const NavLinkSeparator = (props: NavLinkSeparatorProps) => (
  <DropdownMenu.Separator {...props} />
)

type WithType<T, U> = {type: T} & Omit<U, 'type'>

export type NavLinkAccountPropsWithType = WithType<
  'account',
  NavLinkAccountProps
>
export type NavLinkBookshelfPropsWithType = WithType<
  'bookshelf',
  NavLinkBookshelfProps
>
export type NavLinkExpandoPropsWithType = WithType<
  'expando',
  NavLinkExpandoProps
>
export type NavLinkLinkPropsWithType = WithType<'link', NavLinkLinkProps>
export type NavLinkMenuPropsWithType = WithType<'menu', NavLinkMenuProps>
export type NavLinkSeparatorPropsWithType = WithType<
  'separator',
  NavLinkSeparatorProps
>

export type NavLinkProps =
  | NavLinkAccountPropsWithType
  | NavLinkBookshelfPropsWithType
  | NavLinkExpandoPropsWithType
  | NavLinkLinkPropsWithType
  | NavLinkMenuPropsWithType
  | NavLinkSeparatorPropsWithType

const _NavLink = (props: NavLinkProps) => {
  switch (props.type) {
    case 'account': {
      const {type, ...rest} = props
      return <NavLinkAccount {...rest} />
    }
    case 'bookshelf': {
      const {type, ...rest} = props
      return <NavLinkBookshelf {...rest} />
    }
    case 'expando': {
      const {type, ...rest} = props
      return <NavLinkExpando {...rest} />
    }
    case 'link': {
      const {type, ...rest} = props
      return <NavLinkLink {...rest} />
    }
    case 'menu': {
      const {type, ...rest} = props
      return <NavLinkMenu {...rest} />
    }
    case 'separator': {
      const {type, ...rest} = props
      return <NavLinkSeparator {...rest} />
    }
    default: {
      const unreachable: never = props
      log.error('unreachable case:', unreachable)
      return (props as any).type ?
          null
        : <NavLinkLink {...(props as any)} type="link" />
    }
  }
}

export const NavLink = Object.assign(_NavLink, {Group: S.NavLinkGroup})
